import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';

import { finalize, take, takeUntil } from 'rxjs';

import { CoreService, DataService, DialogResult, DialogService, FormNavigationService, MessagePlaceholders, SpinnerService } from 'dspx-core';

import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import {
  APP_DATA_KEYS,
  BREADCRUMBS,
  DASHBOARD,
  FILTER,
  FORM_IDENTIFIERS,
  minWaitForDialogToCloseMs,
  NAV_TAB_NUMBERS,
  PAGE_NUMBERS,
  POP_UP_CODES,
  QUESTION_CODES,
  QUESTION_PATHS,
  READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS,
  SAVE_RESULT_STATUS,
  SECTION_CODES,
  SERVER_ERROR_POP_UP_CODES
} from '../../constants/app-data.constants';
import { MaatAuditPlanType } from '../../constants/maat-audit-plan-type.constants';
import { MaatNcqStatus, MaatNcqStatusDescriptions } from '../../constants/maat-ncq-status.constants';
import { DASHBOARD_FILTER_NAMES } from '../../constants/grid-data-filter.constants';

import { MaatJobGridFilterRequest } from '../../interfaces/maat-job-grid-filter-request.interface';
import { MaatJobItemGridFilterRequest } from '../../interfaces/maat-job-item-grid-filter-request.interface';
import { MaatNcqGridFilterRequest } from '../../interfaces/maat-ncq-grid-filter-request.interface';
import { LoggedInUserRoleFlags } from '../../interfaces/logged-in-user-role-flags.interface';
import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';
import { MaatAuditPlanSaveDataRequest } from '../../interfaces/maat-audit-plan-save-data-request.interface';
import { MaatAuditPlanCustomSaveData } from '../../interfaces/maat-audit-plan-custom-save-data.interface';
import { PopupInfo } from '../../interfaces/popup-info.interface';
import { ExportSummaryReportRequest } from '../../interfaces/export-summary-report-request.interface';

import { DateTimeValidator } from '../../helpers/date-time-validator';
import { FileUploadHelper } from '../../helpers/file-upload-helper';
import { MaatAuditPlanAccess } from '../../helpers/maat-audit-plan-access';
import { PagePath } from '../../helpers/config-data-parser';
import { ContactDetails } from '../../helpers/contact-details';
import { MaatNcqAccess } from '../../helpers/maat-ncq-access';
import { GridDataFilterHandler } from '../../helpers/grid-data-filter-handler';
import { GridDataClientHandler } from '../../helpers/grid-data-client-handler';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';
import { ErrorService } from '../../services/error.service';
import { NetworkConnectivityService } from '../../services/network-connectivity.service';
import { HtmlHelperService } from '../../services/html-helper.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

type SetInspectionDatePopupModel = { inspectionDate: string, selectedItems: any[] };

@Component({
  selector: 'app-maat-job-list',
  templateUrl: './maat-job-list.component.html',
  styleUrls: ['./maat-job-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MaatJobListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  private loggedInUserProfile: LoggedInUserProfile;
  private maatJobFilterHandler: GridDataFilterHandler<MaatJobGridFilterRequest>;
  private maatJobItemFilterHandler: GridDataFilterHandler<MaatJobItemGridFilterRequest>;
  private maatNcqFilterHandler: GridDataFilterHandler<MaatNcqGridFilterRequest>;

  private maatJobGridDataClientHandler: GridDataClientHandler;
  private maatJobItemGridDataClientHandler: GridDataClientHandler;
  private maatNcqGridDataClientHandler: GridDataClientHandler;

  activeDialog: string = '';
  maatAuditPlanId: string = '';

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private route: ActivatedRoute,
    private coreService: CoreService,
    private formConfigurationParserService: FormConfigurationParserService,
    private dataService: DataService,
    private errorService: ErrorService,
    private networkConnectivityService: NetworkConnectivityService,
    private excelExportService: ExcelExportService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.maatJobList,
      null,
      true);

    this.initialiseReturnNavigationState();

    this.maatJobFilterHandler = new GridDataFilterHandler<MaatJobGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatJobsByAuditPlan(this.maatAuditPlanId),
        searchQuestionPath: [...QUESTION_PATHS.maatJobList.search],
        filterQuestionPath: [...QUESTION_PATHS.maatJobList.filter],
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          hasContractorDocumentation: filterQuestionModel?.hasContractorDocumentation,
          hasBeenInspected: filterQuestionModel?.hasBeenInspected,
          isNoAccess: filterQuestionModel?.isNoAccess,
          inspectionDateFrom: filterQuestionModel?.inspectionDateFrom,
          inspectionDateTo: filterQuestionModel?.inspectionDateTo,
          suburb: filterQuestionModel?.suburb,
          navTabIndex: NAV_TAB_NUMBERS.maatJobList.maatJobs
        }),
        getDefaultFilterRequest: () => ({
          navTabIndex: NAV_TAB_NUMBERS.maatJobList.maatJobs
        })
      });

    this.maatJobItemFilterHandler = new GridDataFilterHandler<MaatJobItemGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatJobItemsByAuditPlan(this.maatAuditPlanId),
        searchQuestionPath: [...QUESTION_PATHS.maatJobList.jobItemListSearch],
        filterQuestionPath: null,
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          navTabIndex: NAV_TAB_NUMBERS.maatJobList.maatJobItems
        }),
        getDefaultFilterRequest: () => ({
          navTabIndex: NAV_TAB_NUMBERS.maatJobList.maatJobItems
        })
      });

    this.maatNcqFilterHandler = new GridDataFilterHandler<MaatNcqGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatNcqsByAuditPlan(this.maatAuditPlanId),
        searchQuestionPath: [...QUESTION_PATHS.maatJobList.ncqListSearch],
        filterQuestionPath: [...QUESTION_PATHS.maatJobList.ncqListFilter],
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          ncqStatuses: filterQuestionModel?.ncqStatuses,
          category: filterQuestionModel?.category,
          headContractorPosition: filterQuestionModel?.headContractorPosition,
          businessPosition: filterQuestionModel?.businessPosition,
          tradeCode: filterQuestionModel?.tradeCode,
          responseRequiredByFrom: filterQuestionModel?.responseRequiredByFrom,
          responseRequiredByTo: filterQuestionModel?.responseRequiredByTo,
          navTabIndex: NAV_TAB_NUMBERS.maatJobList.maatNcqs
        }),
        getDefaultFilterRequest: () => ({
          ncqStatuses: this.getMaatNcqAccess().getDefaultNcqStatuses(),
          navTabIndex: NAV_TAB_NUMBERS.maatJobList.maatNcqs
        })
      });

    this.maatJobGridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatJobList.placeholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatJobsByAuditPlan(this.maatAuditPlanId));

    this.maatJobItemGridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatJobList.jobItemListPlaceholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatJobItemsByAuditPlan(this.maatAuditPlanId));

    this.maatNcqGridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatJobList.ncqListPlaceholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatNcqsByAuditPlan(this.maatAuditPlanId));
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap: ParamMap) => {
      this.maatAuditPlanId = paramMap.get('maatAuditPlanId');
      if (this.maatAuditPlanId) {
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getMaatAuditPlanConfigAndResponse;
      }
    });

    this.coreService.conditionFunctions['isReadOnly'] = () => !this.canEditAuditPlan();
    this.coreService.conditionFunctions['alwaysTrue'] = () => true;

    this.coreService.applicationFunctions['canRequestDocumentation'] = () => this.canRequestDocumentation();
    this.coreService.applicationFunctions['requestDocumentation'] = () => this.requestDocumentation();

    this.coreService.applicationFunctions['canSubmitDocumentation'] = () => this.canSubmitDocumentation();
    this.coreService.applicationFunctions['submitDocumentation'] = () => this.submitDocumentation();

    this.coreService.applicationFunctions['canReferToSeniorProjectOfficer'] = () => this.canReferToSeniorProjectOfficer();
    this.coreService.applicationFunctions['referToSeniorProjectOfficer'] = () => this.referToSeniorProjectOfficer();

    this.coreService.applicationFunctions['canReferToManager'] = () => this.canReferToManager();
    this.coreService.applicationFunctions['referToManager'] = () => this.referToManager();

    this.coreService.applicationFunctions['canReturnToProjectOfficer'] = () => this.canReturnToProjectOfficer();
    this.coreService.applicationFunctions['returnToProjectOfficer'] = () => this.returnToProjectOfficer();

    this.coreService.applicationFunctions['canCompleteAuditPlan'] = () => this.canCompleteAuditPlan();
    this.coreService.applicationFunctions['completeAuditPlan'] = () => this.completeAuditPlan();

    this.coreService.applicationFunctions['canReturnToSeniorProjectOfficer'] = () => this.canReturnToSeniorProjectOfficer();
    this.coreService.applicationFunctions['returnToSeniorProjectOfficer'] = () => this.returnToSeniorProjectOfficer();

    this.coreService.applicationFunctions['canFinaliseAuditPlan'] = () => this.canFinaliseAuditPlan();
    this.coreService.applicationFunctions['finaliseAuditPlan'] = () => this.finaliseAuditPlan();

    this.coreService.applicationFunctions['canUndoCompleteAuditPlan'] = () => this.canUndoCompleteAuditPlan();
    this.coreService.applicationFunctions['undoCompleteAuditPlan'] = () => this.undoCompleteAuditPlan();

    this.coreService.applicationFunctions['canExportSummaryReport'] = () => this.canExportSummaryReport();
    this.coreService.applicationFunctions['exportSummaryReport'] = () => this.exportSummaryReport();

    this.coreService.applicationFunctions['canShowSaveButton'] = () => this.canShowSaveButton();

    this.coreService.applicationFunctions['validateInspectionDate'] = (args: any[]) =>
      DateTimeValidator.validateDateFormat(args);

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker().then(waitResult => {

      this.loggedInUserProfile = waitResult.loggedInUserProfile;

      this.maatJobFilterHandler.setInitialFilterRequest();
      this.maatJobItemFilterHandler.setInitialFilterRequest();
      this.maatNcqFilterHandler.setInitialFilterRequest();
    });
  }

  ngOnDestroy(): void {

    this.maatJobGridDataClientHandler.unsubscribe();
    this.maatJobItemGridDataClientHandler.unsubscribe();
    this.maatNcqGridDataClientHandler.unsubscribe();

    this.completeDestroy();
  }

  cancel(event: any): void {
    this.navigateBackToDashboard();
  }

  formLoad(event: any): void {
    const parentId = this.maatAuditPlanId || '';
    const additionalData = event?.customConfigData?.[parentId] ?? {};
    const { maatAuditPlanHeading, filterSuburbs } = additionalData;
    this.appDataService.setBreadcrumbs([
      { label: BREADCRUMBS.pageHeadings.auditPlans, url: ROUTE_CONSTANTS.maatAuditPlan.list },
      { label: maatAuditPlanHeading }
    ]);

    this.configData = event.configData;
    this.fileUploadQuestions = FileUploadHelper.getFileUploadQuestionGetModelFunctions(this.formConfigurationParserService, this.responseParserService, this.baseForm);
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.maatJobList.documentation] = FORM_IDENTIFIERS.maatAuditPlan.documentation;

    // On the form load event the base form model is populated with the audit plan details, but the model remains undefined.
    // We set the model to the base form model to ensure that audit plan details are populated along with the grid data.
    this.model = this.baseForm.model;
    this.toggleJobGridColumns();

    this.maatJobGridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.maatJobGridDataClientHandler.subscribe())
      .then(() => this.loadJobGridData(() => this.maatJobFilterHandler.getInitialFilterRequest()));

    this.maatJobItemGridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.maatJobItemGridDataClientHandler.subscribe())
      .then(() => this.loadJobItemsGridData(() => this.maatJobItemFilterHandler.getInitialFilterRequest()));

    this.maatNcqGridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.maatNcqGridDataClientHandler.subscribe())
      .then(() => this.loadNcqsGridData(() => this.maatNcqFilterHandler.getInitialFilterRequest()));

    this.setAllTabsHeading(maatAuditPlanHeading);
    this.setJobFilterOptions(filterSuburbs ?? []);
    this.modelKeysToIgnore = [
      QUESTION_CODES.maatJobList.maatJobPlaceholder,
      QUESTION_CODES.maatJobList.maatJobItemPlaceholder,
      QUESTION_CODES.maatJobList.maatNcqPlaceholder,
      QUESTION_CODES.maatJobList.maatJobSearch,
      QUESTION_CODES.maatJobList.maatJobFilter,
      QUESTION_CODES.maatJobList.maatJobItemSearch,
      QUESTION_CODES.maatJobList.maatNcqSearch,
      QUESTION_CODES.maatJobList.maatNcqFilter
    ]

    const pagePaths: PagePath[] = [
      [NAV_TAB_NUMBERS.maatJobList.maatAuditPlan, PAGE_NUMBERS.maatJobList.maatAuditPlan],
      [NAV_TAB_NUMBERS.maatJobList.maatJobs, PAGE_NUMBERS.maatJobList.maatJobs],
      [NAV_TAB_NUMBERS.maatJobList.maatJobItems, PAGE_NUMBERS.maatJobList.maatJobItems],
      [NAV_TAB_NUMBERS.maatJobList.maatNcqs, PAGE_NUMBERS.maatJobList.maatNcqs]
    ];
    this.ensurePageActionButtonsAreEnabledOnFormLoad(pagePaths, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.maatJobList);

    this.setPreviousModel({ isAsync: true });

    this.restoreReturnActiveTab();
  }

  save(event: any): void {

    if (!this.canShowSaveButton()) {
      return;
    }

    const saveDataRequest = this.getSaveDataRequest();
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showPopup(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  referToSeniorProjectOfficer(): void {
    const eligibleNcqStatuses = [
        MaatNcqStatusDescriptions[MaatNcqStatus.deleted],
        MaatNcqStatusDescriptions[MaatNcqStatus.referredToSpo],
        MaatNcqStatusDescriptions[MaatNcqStatus.spoInformationRequested],
        MaatNcqStatusDescriptions[MaatNcqStatus.spoInformationOverdue],
        MaatNcqStatusDescriptions[MaatNcqStatus.spoInformationProvided],
        MaatNcqStatusDescriptions[MaatNcqStatus.referredToMgr],
        MaatNcqStatusDescriptions[MaatNcqStatus.completed]
      ];

    this.saveDataWithConfirmationIfNcqsAreEligible(
      eligibleNcqStatuses,
      POP_UP_CODES.maatJobList.referToSeniorProjectOfficerConfirmation,
      { isReferToSeniorProjectOfficer: true },
      POP_UP_CODES.maatJobList.referToSeniorProjectOfficerSuccessful,
      POP_UP_CODES.maatJobList.referToSeniorProjectOfficerIneligibleNcqsStatus
    );
  }

  referToManager(): void {
    const eligibleNcqStatuses = [
      MaatNcqStatusDescriptions[MaatNcqStatus.deleted],
      MaatNcqStatusDescriptions[MaatNcqStatus.referredToMgr],
      MaatNcqStatusDescriptions[MaatNcqStatus.completed]
    ];

    this.saveDataWithConfirmationIfNcqsAreEligible(
      eligibleNcqStatuses,
      POP_UP_CODES.maatJobList.referToManagerConfirmation,
      { isReferToManager: true },
      POP_UP_CODES.maatJobList.referToManagerSuccessful,
      POP_UP_CODES.maatJobList.referToManagerIneligibleNcqsStatus
    );
  }

  returnToProjectOfficer(): void {
    this.saveDataWithConfirmation(
      POP_UP_CODES.maatJobList.returnToProjectOfficerConfirmation,
      { isReturnToProjectOfficer: true },
      POP_UP_CODES.maatJobList.returnToProjectOfficerSuccessful
    );
  }

  completeAuditPlan(): void {
     const eligibleNcqStatuses = [
      MaatNcqStatusDescriptions[MaatNcqStatus.deleted],
      MaatNcqStatusDescriptions[MaatNcqStatus.completed]
    ];

    this.saveDataWithConfirmationIfNcqsAreEligible(
      eligibleNcqStatuses,
      POP_UP_CODES.maatJobList.completeAuditPlanConfirmation,
      { isCompleteAuditPlan: true },
      POP_UP_CODES.maatJobList.completeAuditPlanSuccessful,
      POP_UP_CODES.maatJobList.completeAuditPlanIneligibleNcqsStatus
    );
  }

  returnToSeniorProjectOfficer(): void {
    this.saveDataWithConfirmation(
      POP_UP_CODES.maatJobList.returnToSeniorProjectOfficerConfirmation,
      { isReturnToSeniorProjectOfficer: true },
      POP_UP_CODES.maatJobList.returnToSeniorProjectOfficerSuccessful
    );
  }

  finaliseAuditPlan(): void {
    this.saveDataWithConfirmation(
      POP_UP_CODES.maatJobList.finaliseAuditPlanConfirmation,
      { isFinaliseAuditPlan: true },
      POP_UP_CODES.maatJobList.finaliseAuditPlanSuccessful
    );
  }

  undoCompleteAuditPlan(): void {
    this.saveDataWithConfirmation(
      POP_UP_CODES.maatJobList.undoCompleteAuditPlanConfirmation,
      { isUndoCompleteAuditPlan: true },
      POP_UP_CODES.maatJobList.undoCompleteAuditPlanSuccessful
    );
  }

  exportSummaryReport(): void {
    if (!this.networkConnectivityService.isOnline()) {
      this.sharedSimplePopup.showNotSupportedWhenOfflinePopup();
      return;
    }
    if (this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags).isMaatHeadContractor) {
      this.sharedSimplePopup.showNoPermissionPopup();
      return;
    }
    const summaryReportRequest: ExportSummaryReportRequest = { entityId: this.maatAuditPlanId  };
    this.excelExportService.exportMaatAuditPlanSummaryReportToXlsx(summaryReportRequest);
  }

  private saveDataWithConfirmationIfNcqsAreEligible(
    eligibleNcqStatuses: string[],
    confirmationPopupCode: string,
    customSaveData: MaatAuditPlanCustomSaveData,
    successPopupCode: string,
    ineligibleNcqsStatusPopupCode: string
  ): void {
    const ncqs: any[] = this.getQuestionValue(this.model, ...QUESTION_PATHS.maatJobList.ncqListPlaceholder) ?? [];
    const areAllNcqsEligible = ncqs.every(ncq => eligibleNcqStatuses.includes(ncq.ncqStatus));

    if (areAllNcqsEligible) {
      this.saveDataWithConfirmation(
        confirmationPopupCode,
        customSaveData,
        successPopupCode
      );
    }
    else {
      this.dialogService.showPopup(ineligibleNcqsStatusPopupCode);
    }
  }

  private saveDataWithConfirmation(
    confirmationPopupCode: string,
    customSaveData: MaatAuditPlanCustomSaveData,
    successPopupCode: string
  ): void {
    this.dialogService.showConfirmPopup(confirmationPopupCode)
      .pipe(take(1))
      .subscribe(data => {
        if (data === DialogResult.ACCEPTED) {
          const saveDataRequest = this.getSaveDataRequest(customSaveData);
          this.saveData(saveDataRequest, successPopupCode);
        }
      });
  }

  private loadJobGridData(getfilterRequest = this.maatJobFilterHandler.getFilterRequest): void {
    const onGridLoaded = () => this.maatJobGridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(getfilterRequest(), onGridLoaded, NAV_TAB_NUMBERS.maatJobList.maatJobs, `${API_ENDPOINTS.dashboard.getMaatJobs}/${this.maatAuditPlanId}`);
  }

  private loadJobItemsGridData(getfilterRequest = this.maatJobItemFilterHandler.getFilterRequest): void {
    const onGridLoaded = () => this.maatJobItemGridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(getfilterRequest(), onGridLoaded, NAV_TAB_NUMBERS.maatJobList.maatJobItems, `${API_ENDPOINTS.dashboard.getMaatJobItemsByAuditPlan}/${this.maatAuditPlanId}`);
  }

  private loadNcqsGridData(getfilterRequest = this.maatNcqFilterHandler.getFilterRequest): void {
    const onGridLoaded = () => this.maatNcqGridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(getfilterRequest(), onGridLoaded, NAV_TAB_NUMBERS.maatJobList.maatNcqs, `${API_ENDPOINTS.dashboard.getMaatNcqsByAuditPlan}/${this.maatAuditPlanId}`);
  }

  // If offline we can set each nav tab heading
  private setAllTabsHeading(heading: string): void {
    const pageConfigs = [
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobList.maatAuditPlan, PAGE_NUMBERS.maatJobList.maatAuditPlan),
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobList.maatJobs, PAGE_NUMBERS.maatJobList.maatJobs),
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobList.maatJobItems, PAGE_NUMBERS.maatJobList.maatJobItems),
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobList.maatNcqs, PAGE_NUMBERS.maatJobList.maatNcqs)
    ]
      .filter(pageConfig => !!pageConfig); // pages might be missing depending on the logged-in user role

    pageConfigs.forEach(pageConfig => {
      pageConfig.props.heading = heading;
    });
  }

  private setJobFilterOptions(suburbs: any[]): void {
    const filterQuestionConfig = this.formConfigurationParserService.findQuestionConfig(
      this.baseForm.fields,
      NAV_TAB_NUMBERS.maatJobList.maatJobs,
      PAGE_NUMBERS.maatJobList.maatJobs,
      SECTION_CODES.maatJobList.maatJob,
      QUESTION_CODES.maatJobList.maatJobFilter
    );

    const setFilterOptions = (filterKey: string, options: any[]) => {
      const filterConfig = filterQuestionConfig?.fieldGroup?.find((f: any) => f.key === filterKey);
      if (filterConfig?.props) {
        filterConfig.props.options = options;
      }
    }

    setFilterOptions(FILTER.maatJobList.suburb, suburbs);
  }

  private toggleJobGridColumns(): void {
    const planType = this.getQuestionValue(this.model, ...QUESTION_PATHS.maatJobList.planType)?.toLowerCase();
    this.toggleJobGridColumn(DASHBOARD.maatJob.column.totalItems, planType === MaatAuditPlanType.Random);
  }

  private toggleJobGridColumn(columnField: string, visible: boolean): void {
    const questionConfig = this.formConfigurationParserService.findQuestionConfig(
      this.baseForm.fields,
      NAV_TAB_NUMBERS.maatJobList.maatJobs,
      PAGE_NUMBERS.maatJobList.maatJobs,
      SECTION_CODES.maatJobList.maatJob,
      QUESTION_CODES.maatJobList.maatJobPlaceholder
    );

    const column = questionConfig?.props?.dashboardOptions?.cols?.find((column: any) => column.field === columnField);

    if (column) {
      column.visible = visible;
    }
  }

  filterData(event: any): void {
    const searchKey = event?.field?.key;

    switch (searchKey) {
      case QUESTION_CODES.maatJobList.maatJobSearch:
      case QUESTION_CODES.maatJobList.maatJobFilter:
        this.loadJobGridData(() => this.maatJobFilterHandler.saveFilterRequest(event.action));
        break;
      case QUESTION_CODES.maatJobList.maatJobItemSearch:
        this.loadJobItemsGridData(() => this.maatJobItemFilterHandler.saveFilterRequest(event.action));
        break;
      case QUESTION_CODES.maatJobList.maatNcqSearch:
      case QUESTION_CODES.maatJobList.maatNcqFilter: {
        this.loadNcqsGridData(() => this.maatNcqFilterHandler.saveFilterRequest(event.action));
        break;
      }
      default:
        throw new Error(`Unknown search key: ${searchKey}`);
    }
  }

  private saveData(request: MaatAuditPlanSaveDataRequest, saveSuccessfulPopupCode?: string): void {

    const errorPopupCode = this.performCustomValidations(request);
    if (errorPopupCode) {
      setTimeout(() => this.dialogService.showConfirmPopup(errorPopupCode), minWaitForDialogToCloseMs);
      return;
    }

    const customErrorPopups = new Map<HttpStatusCode, PopupInfo>([
      [HttpStatusCode.Forbidden, { popupCode: POP_UP_CODES.common.saveForbidden }]
    ]);
    this.spinnerService.showSpinner();
    this.dataService.saveData(API_ENDPOINTS.maatAuditPlan.save, request)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(customErrorPopups),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (data?.status === SAVE_RESULT_STATUS.common.success) {
          if (SERVER_ERROR_POP_UP_CODES.saveMaatAuditPlan.includes(data.message)) {
            this.activeDialog = data.message;
          }
          else {
            this.maatAuditPlanId = data.id;
            this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
            this.activeDialog = saveSuccessfulPopupCode ?? POP_UP_CODES.common.saveSuccessful;
          }
          this.dialogService.showPopup(this.activeDialog);
        }
      });
  }

  private performCustomValidations(request: MaatAuditPlanSaveDataRequest): string {

    if (request?.customSaveData?.isRequestDocumentation) {
      const getContactDetailsQuestion = (questionCode: string) => this.getQuestionValue(
        this.model,
        NAV_TAB_NUMBERS.maatJobList.maatAuditPlan,
        PAGE_NUMBERS.maatJobList.maatAuditPlan,
        SECTION_CODES.maatJobList.maatAuditPlanDetails,
        questionCode);

      const complianceOfficerContactDetails = new ContactDetails(
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.complianceOfficerUserFirstName),
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.complianceOfficerUserLastName),
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.complianceOfficerUserEmail),
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.complianceOfficerUserContactPhone));
      if (!complianceOfficerContactDetails.hasContactDetails()) {
        return POP_UP_CODES.maatJobList.requestDocumentationMissingComplianceOfficerDetails;
      }

      const contractorContactDetails = new ContactDetails(
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.contractorUserFirstName),
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.contractorUserLastName),
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.contractorUserEmail),
        getContactDetailsQuestion(QUESTION_CODES.maatJobList.contractorUserContactPhone));
      if (!contractorContactDetails.hasContactDetails()) {
        return POP_UP_CODES.maatJobList.requestDocumentationMissingContractorDetails;
      }
    }

    return null;
  }

  actionButton_maatJobPlaceholder_datagrid_details_Clicked(event: any): void {
    const maatJobId = event.event.data.id;
    this.navigate(['/', ROUTE_CONSTANTS.maatJobItem.listPrefix, this.maatAuditPlanId, maatJobId]);
  }

  // Confusingly, the event is named "doubleClick", but it is actually triggered when a checkbox is ticked (either via mouse click or keyboard selection).
  actionButton_maatJobPlaceholder_datagrid_doubleClick_Clicked(event: any): void {
    // No action required
  }

  actionButton_maatJobPlaceholder_datagrid_custom_setInspectionDate_Clicked(event: any): void {
    if (!this.networkConnectivityService.isOnline()) {
      this.sharedSimplePopup.showNotSupportedWhenOfflinePopup();
      return;
    }
    if (!this.getMaatAuditPlanAccess().canSetInspectionDate()) {
      this.sharedSimplePopup.showNoPermissionPopup();
      return;
    }
    const selectedItems = this.getGridSelectedItems(event);
    if (selectedItems.length === 0) {
      this.sharedSimplePopup.showNoItemsSelectedPopup();
      return;
    }
    const popupModel: SetInspectionDatePopupModel = { inspectionDate: null, selectedItems };
    this.openSetInspectionDatePopup(popupModel);
  }

  actionButton_maatJobItemPlaceholder_datagrid_details_Clicked(event: any): void {
    const maatJobItemId = event.event.data.id;
    const maatJobId = event.event.data.maatJobId;
    this.navigate(
      ['/', ROUTE_CONSTANTS.maatNcq.listPrefix, this.maatAuditPlanId, maatJobId, maatJobItemId],
      false,
      {
        state: {
          returnRoute: ['/', ROUTE_CONSTANTS.maatJob.listPrefix, this.maatAuditPlanId],
          activeTab: NAV_TAB_NUMBERS.maatJobList.maatJobItems
        }
      }
    );
  }

  actionButton_maatNcqPlaceholder_datagrid_details_Clicked(event: any): void {
    const maatNcqId = event.event.data.id;
    const maatJobId = event.event.data.maatJobId;
    const maatJobItemId = event.event.data.maatJobItemId;
    this.navigate(
      ['/', ROUTE_CONSTANTS.maatNcq.prefix, this.maatAuditPlanId, maatJobId, maatJobItemId, maatNcqId],
      false,
      {
        state: {
          returnRoute: ['/', ROUTE_CONSTANTS.maatJob.listPrefix, this.maatAuditPlanId],
          activeTab: NAV_TAB_NUMBERS.maatJobList.maatNcqs
        }
      }
    );
  }

  actionButton_maatNcqPlaceholder_datagrid_custom_issueNcqs_Clicked(event: any): void {
    if (!this.networkConnectivityService.isOnline()) {
      this.sharedSimplePopup.showNotSupportedWhenOfflinePopup();
      return;
    }

    if (!this.getMaatNcqAccess().canIssueNewNcqs()) {
      this.sharedSimplePopup.showNoPermissionPopup();
      return;
    }
    
    this.activeDialog = POP_UP_CODES.maatJobList.issueNcsqBulk;
    this.dialogService.showPopup(this.activeDialog);
  }

  actionButton_maatNcqPlaceholder_datagrid_custom_reIssueReferredNcqs_Clicked(event: any): void {
    if (!this.networkConnectivityService.isOnline()) {
      this.sharedSimplePopup.showNotSupportedWhenOfflinePopup();
      return;
    }

    if (!this.getMaatNcqAccess().canReIssueNcqs()) {
      this.sharedSimplePopup.showNoPermissionPopup();
      return;
    }

    this.activeDialog = POP_UP_CODES.maatJobList.reIssueReferredNcsqBulk;
    this.dialogService.showPopup(this.activeDialog);
  }

  dialogButton_saveSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_requestDocumentationSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_submitDocumentationSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_referToSeniorProjectOfficerSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_finaliseAuditPlanSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_undoCompleteAuditPlanSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_setInspectionDate_Clicked(event: any): void {
    this.activeDialog = '';
    const popupModel: SetInspectionDatePopupModel = event;
    this.setInspectionDate(popupModel);
  }

  dialogButton_issueNcsqBulk_Clicked(event: any): void {
    this.activeDialog = '';
    this.issueNcqBulkAction(
      API_ENDPOINTS.maatNcq.bulkIssueNcqs,
      {
        maatAuditPlanId: this.maatAuditPlanId
      });
  }

  dialogButton_reIssueReferredNcsqBulk_Clicked(event: any): void {
    this.activeDialog = '';
    this.issueNcqBulkAction(
      API_ENDPOINTS.maatNcq.bulkReIssueNcqs,
      {
        maatAuditPlanId: this.maatAuditPlanId
      });
  }

  dialogButton_referToManagerSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_returnToProjectOfficerSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_completeAuditPlanSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_returnToSeniorProjectOfficerSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }
  
  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.maatAuditPlan.list], ignoreUnsavedChanges);
  }

  private openSetInspectionDatePopup(popupModel: SetInspectionDatePopupModel): void {
    this.activeDialog = POP_UP_CODES.maatJobList.setInspectionDate;
    const messagePlaceholders: MessagePlaceholders = { selectedItemCount: popupModel.selectedItems.length.toString() };
    this.dialogService.showPopup(this.activeDialog, popupModel, messagePlaceholders);
  }

  private setInspectionDate(popupModel: SetInspectionDatePopupModel): void {

    const { inspectionDate, selectedItems } = popupModel;
    const maatJobIds: string[] = selectedItems.map(item => item.id);

    const payload = { maatJobIds, inspectionDate };
    const apiEndPoint = API_ENDPOINTS.maatJob.setInspectionDate;

    this.spinnerService.showSpinner();
    this.appDataService.post(apiEndPoint, payload)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner()))
      .subscribe(data => {
        if (data?.status === SAVE_RESULT_STATUS.common.success) {
          if (SERVER_ERROR_POP_UP_CODES.setMaatJobInspectionDate.includes(data.message)) {
            this.dialogService.showConfirmPopup(data.message)
              .pipe(take(1))
              .subscribe(data => {
                setTimeout(() => this.openSetInspectionDatePopup(popupModel), minWaitForDialogToCloseMs);
              });
          }
          else {
            this.dialogService.showConfirmPopup(POP_UP_CODES.maatJobList.setInspectionDateSuccess)
              .pipe(take(1))
              .subscribe(dialogResult => {
                if (data.planStatus) {
                  this.setQuestionValue(this.model, ...QUESTION_PATHS.maatJobList.planStatus, data.planStatus);
                }
                this.loadJobGridData();
              });
          }
        }
      });
  }

  private canRequestDocumentation(): boolean {
    return this.getMaatAuditPlanAccess().canRequestDocumentation();
  }

  private getMaatAuditPlanAccess(): MaatAuditPlanAccess {
    return new MaatAuditPlanAccess(
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags),
      this.getQuestionValue(this.model, ...QUESTION_PATHS.maatJobList.planStatus)
    );
  }

  private getMaatNcqAccess(): MaatNcqAccess {
    return new MaatNcqAccess(
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags)
    );
  }

  private requestDocumentation(): void {
    this.saveDataWithConfirmation(
      POP_UP_CODES.maatJobList.requestDocumentationConfirmation,
      { isRequestDocumentation: true },
      POP_UP_CODES.maatJobList.requestDocumentationSuccessful
    );
  }

  private getSaveDataRequest(customSaveData?: MaatAuditPlanCustomSaveData): MaatAuditPlanSaveDataRequest {
    const saveDataRequest: MaatAuditPlanSaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.maatAuditPlanId,
      response: JSON.stringify(this.model),
      expectedLastUpdateTime: this.baseForm.lastUpdatedTime,
      isSubmit: false,
      customSaveData: { ...customSaveData }
    };
    return saveDataRequest;
  }

  private canEditAuditPlan(): boolean {
    return this.getMaatAuditPlanAccess().canEdit();
  }

  private canShowSaveButton(): boolean {
    return this.getMaatAuditPlanAccess().canSave();
  }

  private canSubmitDocumentation(): boolean {
    return this.getMaatAuditPlanAccess().canSubmitDocumentation();
  }

  private canReferToSeniorProjectOfficer(): boolean {
    return this.getMaatAuditPlanAccess().canReferToSeniorProjectOfficer();
  }

  private canReferToManager(): boolean {
    return this.getMaatAuditPlanAccess().canReferToManager();
  }

  private canReturnToProjectOfficer(): boolean {
    return this.getMaatAuditPlanAccess().canReturnToProjectOfficer();
  }

  private canCompleteAuditPlan(): boolean {
    return this.getMaatAuditPlanAccess().canComplete();
  }

  private canReturnToSeniorProjectOfficer(): boolean {
    return this.getMaatAuditPlanAccess().canReturnToSeniorProjectOfficer();
  }

  private canFinaliseAuditPlan(): boolean {
    return this.getMaatAuditPlanAccess().canFinalise();
  }

  private canUndoCompleteAuditPlan(): boolean {
    return this.getMaatAuditPlanAccess().canUndoComplete();
  }

  private canExportSummaryReport(): boolean {
    return this.getMaatAuditPlanAccess().canExportSummaryReport();
  }

  private submitDocumentation(): void {
    this.saveDataWithConfirmation(
      POP_UP_CODES.maatJobList.submitDocumentationConfirmation,
      { isSubmitDocumentation: true },
      POP_UP_CODES.maatJobList.submitDocumentationSuccessful
    );
  }
  
  private issueNcqBulkAction(
    apiEndPoint: string,
    payload: any
  ): void {
    this.spinnerService.showSpinner();
    this.appDataService.post(apiEndPoint, payload)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (data?.status === SAVE_RESULT_STATUS.common.success) {
          if (SERVER_ERROR_POP_UP_CODES.bulkIssueNcqs.includes(data.message)) {
            this.activeDialog = data.message;
          }
          else {
            this.loadNcqsGridData();
            this.activeDialog = POP_UP_CODES.maatJobList.issueNcqBulkSuccess;
          }
          this.dialogService.showPopup(this.activeDialog);
        }
      });
  }
}
